import {
  Attribute,
  Button,
  Column,
  DATE_FORMATTER_STR_WITH_TIME,
  formatDate,
  Header,
  Row,
  ScrollContainer,
  Section,
  StickyFooterButtons,
  TeamLogo,
  useLoading,
} from "best-common-react";

import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { createPurchaseEntry, getPurchaseOpportunity } from "../../api/RequesTixApi";
import PurchaseOpportunityForm from "../../components/purchaseOpportunity/PurchaseOpportunityForm";
import RouteConstants from "../../constants/RouteConstants";
import { PurchaseOpportunityDTO, PurchaseOpportunityEntryRecordDTO } from "../../types/PurchaseOpportunity";
import { useQueryClient } from "@tanstack/react-query";
import { queries } from "../../queries";

type RouteParams = {
  id: string;
};

const PurchaseOpportunityRequest: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<RouteParams>();
  const { setLoading } = useLoading();
  const [opportunity, setOpportunity] = useState<PurchaseOpportunityDTO | undefined>();
  const [entry, setEntry] = useState<PurchaseOpportunityEntryRecordDTO>({ purchaseOpportunityGameIds: [] });
  const [canSave, setCanSave] = useState<boolean>(false);
  const queryClient = useQueryClient();

  const fetchPurchaseOpportunity = async (id: string) => {
    try {
      setLoading(true);
      const res: PurchaseOpportunityDTO = await getPurchaseOpportunity(id);
      setOpportunity(res);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const saveRequest = async () => {
    try {
      setLoading(true);
      await createPurchaseEntry(entry);
      await queryClient.invalidateQueries({
        queryKey: queries.purchaseOpportunity._def,
      });
      navigate(RouteConstants.PURCHASE_OPPORTUNITY.LIST);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const cancel = () => {
    navigate(-1);
  };

  useEffect(() => {
    setEntry({
      ...entry,
      purchaseOpportunityId: parseInt(id!),
    });
    void fetchPurchaseOpportunity(id!);
  }, [id]);

  return (
    <div>
      <Header sticky title="Purchase Opportunity for Pair of Tickets">
        <StickyFooterButtons startsOpen={true}>
          <Button variant="primary" disabled={!canSave} onClick={saveRequest}>
            Submit
          </Button>
          <Button variant="default" onClick={cancel}>
            Cancel
          </Button>
        </StickyFooterButtons>
      </Header>
      <ScrollContainer className="bcr-p-2 bcr-pb-5">
        {!!opportunity && (
          <>
            <Section sectionKey="opportunity-details" header="Opportunity Details" showToggle={false}>
              <Row>
                <Column width={3}>
                  <div className="d-flex align-items-center bcr-gap-2">
                    <TeamLogo teamId={opportunity.teamId!} size="2.5rem" />
                    <Attribute header="Type" value={opportunity.seriesType?.description} />
                  </div>
                </Column>
                <Column width={3}>
                  <Attribute header="Year" value={opportunity.year?.toString()} />
                </Column>
                <Column width={3}>
                  <Attribute header="Number of Winners" value={opportunity.numOfWinners?.toString()} />
                </Column>
                <Column width={3}>
                  <Attribute
                    header="Games"
                    value={[...(opportunity.gameNumbers ?? [])]
                      .sort((a, b) => a - b)
                      .map((game) => `Home Game #${game}`)
                      .join(", ")}
                  />
                </Column>
                <Column width={3}>
                  <Attribute
                    header="Cutoff Date"
                    value={
                      opportunity.cutoffDate
                        ? formatDate(new Date(opportunity.cutoffDate), DATE_FORMATTER_STR_WITH_TIME)
                        : undefined
                    }
                  />
                </Column>
                <Column width={3}>
                  <Attribute header="Cost for a Pair of Tickets" value={opportunity.costDescription} />
                </Column>
              </Row>
            </Section>
            <Section className="bcr-mt-2" sectionKey="request-details" header="Request Details" showToggle={false}>
              <PurchaseOpportunityForm
                opportunity={opportunity}
                entry={entry}
                setEntry={setEntry}
                setValidForm={setCanSave}
              />
            </Section>
          </>
        )}
      </ScrollContainer>
    </div>
  );
};

export default PurchaseOpportunityRequest;
